<template>
  <div class="d-flex flex-column h-100" style="font-family: var(--default-font)">
    <div v-if="label" class="tw-mb-1">
      <lazy-library-text :text="label" tag="span" type="input-label"></lazy-library-text>
      <span v-if="isRequired" style="color: #ff0000; font-size: 14px">*</span>
    </div>
    <div v-if="type === 'textarea'">
      <div class="form-field-wrapper">
        <textarea
          v-model="childModelValue"
          :name="name"
          :placeholder="placeholder"
          :maxlength="maxLength"
          style="border: 1px solid var(--default-border-color); width: 100%; padding: 20px; outline: none"
          class="nc-textarea tw-placeholder-[var(--input--placeholder)]"
          :class="inputClass"
        ></textarea>
      </div>
    </div>
    <div
      v-else-if="!isCustomValueBinding"
      class="n-input n-input-with-icon tw-h-full"
      :class="selectInputClasses"
      style="height: 60px !important"
    >
      <span v-if="inputIcon === 'left'" class="suffix" v-html="inputIconValue"></span>
      <input
        v-model="childModelValue"
        :name="name"
        :type="type"
        :placeholder="placeholder"
        class="h-100 w-100"
        :class="inputClass"
        @keyup.enter="onKeyupEnter()"
        @keyup="onKeyup()"
        @input="onInput()"
        :autocomplete="autocomplete"
      />
      <span v-if="inputIcon === 'right'" class="right-suffix" v-html="inputIconValue"></span>
    </div>
    <div
      v-else-if="isCustomValueBinding"
      class="n-input h-100"
      :class="selectInputClasses"
      style="height: 60px !important"
    >
      <input
        :name="name"
        :type="type"
        :value="value"
        :placeholder="placeholder"
        class="h-100 w-100"
        @keyup.enter="onKeyupEnter()"
        @keyup="onKeyup()"
        @input="onInput()"
        v-on="inputEvents"
        :autocomplete="autocomplete"
      />
    </div>
    <span v-if="maxLength" class="custom-position tw-ms-2" style="background-color: var(--c-input_neuron)">
      {{ childModelValue.length }} / {{ maxLength }}
    </span>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { mapState } from 'pinia';
import { useWebsiteStore } from '~/units/website/store';

export default defineNuxtComponent({
  name: 'NcInput',

  props: {
    inputClass: {
      required: false,
      type: String,
      default: '',
    },
    isCustomValueBinding: {
      required: false,
      type: Boolean,
      default: false,
    },

    modelValue: {
      required: false,
      type: [String, Number],
    },

    label: {
      required: false,
      type: String,
      default: null,
    },

    name: {
      required: false,
      type: String,
      default: null,
    },

    autocomplete: {
      required: false,
      type: String,
      default: undefined,
    },

    placeholder: {
      required: false,
      type: String,
      default: null,
    },

    value: {
      required: false,
    },

    inputEvents: {
      required: false,
    },

    isRequired: {
      required: false,
      type: Boolean,
      default: false,
    },

    type: {
      required: false,
      type: String as PropType<'text' | 'textarea' | 'number' | 'email' | 'tel'>,
      default: 'text',
    },

    maxLength: {
      required: false,
      type: Number,
      default: null,
    },

    inputIcon: {
      required: false,
      type: String as PropType<'left' | 'right' | null>,
      default: null,
    },

    inputIconValue: {
      required: false,
      type: String,
    },
  },

  emits: ['update:modelValue', 'onKeyupEnter', 'onInput', 'onKeyup'],

  computed: {
    ...mapState(useWebsiteStore, ['theme']),

    childModelValue: {
      get() {
        return this.modelValue;
      },
      set(value: string) {
        this.$emit('update:modelValue', value);
      },
    },

    selectInputClasses() {
      switch (this.theme) {
        case 'theme3':
          return '!tw-border !tw-border-[#DDDDDD]';
        default:
          return '';
      }
    },
  },

  methods: {
    onKeyupEnter() {
      this.$emit('onKeyupEnter');
    },

    onKeyup() {
      this.$emit('onKeyup');
    },

    onInput() {
      this.$emit('onInput');
    },
  },
});
</script>

<style scoped>
.n-input {
  font-weight: 400;
  font-size: 16px;
  border: 1px solid #aca39a;
}

.n-input input {
  border: none !important;
  padding-left: 30px;
}

.n-input input::placeholder {
  color: var(--input--placeholder);
  font-weight: 200 !important;
}

.custom-position {
  position: relative;
  top: -30px;
  left: 10px;
}

.n-input-with-icon {
  position: relative;
}

.n-input-with-icon input {
  width: calc(100% - 20px); /* Adjust padding width */
  padding-right: 20px; /* Space for the '%' symbol */
  box-sizing: border-box;
}

.n-input-with-icon .right-suffix {
  position: absolute;
  top: 36%;
  right: 50px; /* Adjust as needed */
  line-height: 100%;
}
</style>
